import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PWAInstallPrompt from '../components/features/PWAInstallPrompt';
import { getCachedShippingCost, setCachedShippingCost, hasValidCache } from '../utils/shippingCostCache';
import { API_BASE_URL } from '../config/api';

const Policies = () => {
    // Get cached shipping cost from localStorage as initial state (with expiration check)
    const [shippingCost, setShippingCost] = useState(getCachedShippingCost());
    
    // Fetch shipping cost from API only if cache is missing or expired
    // The backend updates shipping cost monthly via cron job
    useEffect(() => {
        const fetchShippingCost = async () => {
            // Only fetch if we don't have a valid cache (saves API calls)
            if (hasValidCache()) {
                return; // Use cached value, no API call needed
            }
            
            try {
                // Use simpler endpoint that just returns the stored shipping cost
                const baseUrl = API_BASE_URL.replace('/api/v1', '');
                const response = await fetch(`${baseUrl}/api/orders/get-shipping-cost`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.shippingCost !== null && data.shippingCost !== undefined) {
                        setShippingCost(data.shippingCost);
                        // Cache the value for 30 days
                        setCachedShippingCost(data.shippingCost);
                    } else {
                        // If API doesn't return shipping cost, use cached value as fallback
                        const cached = getCachedShippingCost();
                        if (cached) {
                            setShippingCost(cached);
                        }
                    }
                } else {
                    // API returned error (404, 500, etc.) - use cached value as fallback
                    console.warn('⚠️ Shipping cost API returned error, using cached value');
                    const cached = getCachedShippingCost();
                    if (cached) {
                        setShippingCost(cached);
                    }
                }
            } catch (error) {
                console.error('Error fetching shipping cost:', error);
                // If API fails (network error, etc.) and we have cached value (even if expired), use it as fallback
                const cached = getCachedShippingCost();
                if (cached) {
                    setShippingCost(cached);
                }
            }
        };
        fetchShippingCost();
    }, []);
    
    return (
        <div style={{
            minHeight: '100vh',
            position: 'relative',
            background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
            color: 'white',
            overflow: 'hidden'
        }}>
            <div style={{
                position: 'relative',
                zIndex: 10,
                padding: window.innerWidth <= 768 ? '20px 16px' : '20px',
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
            }}>
                <div style={{ 
                  maxWidth: '1280px', 
                  margin: '0 auto',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                    {/* Featured Image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.4 }}
                        style={{
                            textAlign: 'center',
                            marginBottom: '8px',
                            paddingTop: '20px'
                        }}
                    >
                        <img
                            src="/favicon.png"
                            alt="Rainbow Sails"
                            style={{
                                width: '25%',
                                maxWidth: '200px',
                                height: 'auto',
                                margin: '0 auto'
                            }}
                        />
                    </motion.div>

                    {/* Header */}
                    <header style={{ textAlign: 'center', marginBottom: '64px', paddingTop: '16px' }}>
                        <motion.h1 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            style={{
                                fontSize: 'clamp(3rem, 8vw, 5rem)',
                                fontWeight: 'bold',
                                marginBottom: '24px',
                                color: '#10b981'
                            }}
                        >
                            Policies & Trust
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            style={{
                                fontSize: 'clamp(1.25rem, 3vw, 1.875rem)',
                                color: '#22d3ee',
                                marginBottom: '16px'
                            }}
                        >
                            📋 Everything You Need to Know • 🚚 Shipping & Returns • 🔒 Privacy & Security
                        </motion.p>
                    </header>

                    {/* Shipping Policy Section */}
                    <section id="shipping" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.5), rgba(236, 72, 153, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #10b981'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#10b981' }}>🚚 Shipping Policy</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>Simple Worldwide Shipping</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        We offer one simple shipping rate for everyone, everywhere: {shippingCost ? `$${shippingCost.toFixed(2)}` : 'dynamic'} worldwide shipping on all orders.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>Processing Time</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        All orders are processed within 2-5 business days before shipping. This timeframe allows for the creation 
                                        and fulfillment of your custom products. You'll receive a tracking number as soon as your order ships.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>Delivery Times</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        <strong>Domestic Shipping (US & Canada):</strong> 3-4 business days after fulfillment<br/>
                                        <strong>International Shipping:</strong> 5-20 business days after fulfillment<br/>
                                        <em>Note: Delivery times may vary based on destination and shipping method selected.</em>
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Return & Refund Policy Section */}
                    <section id="returns" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.5), rgba(34, 211, 238, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #22d3ee'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#22d3ee' }}>🔄 Return & Refund Policy</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#7dd3fc' }}>Printful's No-Return Policy</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Due to Printful's manufacturing process and quality standards, we cannot accept returns on most items. 
                                        This policy helps us keep prices affordable while ensuring you receive high-quality products.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#7dd3fc' }}>Quality Guarantee</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        We only work with Printful because of their exceptional quality standards. Every item is 
                                        printed on-demand with premium materials and undergoes quality control before shipping.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#7dd3fc' }}>Damaged or Defective Items</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        If you receive a damaged or defective item, contact us immediately with photos. We'll work with 
                                        Printful to get you a replacement at no cost to you.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#7dd3fc' }}>Why This Policy?</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Printful creates each item specifically for you when you order. This on-demand approach 
                                        reduces waste and keeps costs down, but means we can't resell returned items.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Privacy Policy Section */}
                    <section id="privacy" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.5), rgba(139, 92, 246, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #f472b6'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#f472b6' }}>🔒 Privacy Policy</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#fbcfe8' }}>Information We Collect</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        We only collect information necessary to process your order and provide customer service: 
                                        name, email, shipping address, and payment information.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#fbcfe8' }}>How We Use Your Information</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Your information is used solely to process orders, communicate about your purchase, 
                                        and provide customer support. We never sell or share your personal information.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#fbcfe8' }}>Payment Security</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        All payments are processed securely through Stripe, a trusted payment processor. 
                                        We never store your credit card information on our servers.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#fbcfe8' }}>Your Rights</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        You have the right to access, update, or delete your personal information at any time. 
                                        Contact us if you need to exercise these rights.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Terms of Service Section */}
                    <section id="terms" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.0 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.5), rgba(59, 130, 246, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #3b82f6'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#3b82f6' }}>📜 Terms of Service</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#93c5fd' }}>Order Acceptance</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        All orders are subject to acceptance and availability. We reserve the right to refuse service 
                                        to anyone for any reason at any time.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#93c5fd' }}>Intellectual Property</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        All artwork, designs, and content on this site are the intellectual property of Zee Sails. 
                                        Reproduction or distribution without permission is prohibited.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#93c5fd' }}>Limitation of Liability</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Rainbow Sails is not liable for any indirect, incidental, or consequential damages arising 
                                        from your use of our products or services.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#93c5fd' }}>Governing Law</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        These terms are governed by the laws of Australia. Any disputes will be resolved 
                                        through good faith communication and negotiation.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Customer Service Section */}
                    <section id="customer-service" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.2 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.5), rgba(5, 150, 105, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #10b981'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#10b981' }}>💬 Customer Service</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#6ee7b7' }}>We're Here to Help</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Have questions about our policies or need help with your order? Our customer service team 
                                        is here to help you every step of the way.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#6ee7b7' }}>Contact Methods</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Primary support: sign in on the shop and open My Account → Support (call booking and tools).<br/>
                                        Email: support@rainbow-sails.shop<br/>
                                        Response Time: Within 24 hours during business days
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#6ee7b7' }}>Our Commitment</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        We're committed to providing exceptional customer service and ensuring you have a 
                                        positive experience with every purchase.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Call to Action Section */}
                    <section style={{
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(139, 92, 246, 0.2))',
                        borderRadius: '12px',
                        padding: '32px',
                        border: '2px solid #a78bfa'
                    }}>
                        <motion.h2 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.6 }}
                            style={{
                                fontSize: '2.25rem',
                                fontWeight: 'bold',
                                marginBottom: '16px',
                                color: '#a78bfa'
                            }}
                        >
                            Ready to Shop with Confidence? 🛒
                        </motion.h2>
                        <motion.p 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.7 }}
                            style={{
                                fontSize: '20px',
                                marginBottom: '24px',
                                maxWidth: '48rem',
                                margin: '0 auto 24px'
                            }}
                        >
                            We've got everything covered - from secure payments to quality guarantees. 
                            Shop with confidence knowing your satisfaction and security are our top priorities.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.8 }}
                        >
                            <Link
                                to="/gallery"
                                style={{
                                    display: 'inline-block',
                                    background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                                    color: 'white',
                                    padding: '16px 32px',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    fontSize: '20px',
                                    textDecoration: 'none',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, #db2777, #7c3aed)';
                                    e.target.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, #ec4899, #8b5cf6)';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                🚀 Explore the Gallery
                            </Link>
                        </motion.div>
                        <p style={{ fontSize: '14px', color: '#9ca3af', marginTop: '16px' }}>
                            Secure shopping • {shippingCost ? `$${shippingCost.toFixed(2)}` : 'worldwide'} worldwide shipping • Quality guaranteed • 24/7 support
                        </p>
                    </section>
                </div>
            </div>
            <PWAInstallPrompt />
        </div>
    );
};

export default Policies;